import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  ITEM_STATUS_LABELS_RU,
  ITEM_TRANSITIONS,
  LEGACY_ITEM_STATUSES,
  RequestItemStatusSchema,
} from '@crm/shared'
import type { RequestItemProcurementState } from '@crm/shared'
import { patchItem } from '../../../shared/api/requestItems.api'
import { ItemStatusBadge, ProcItemStatusBadge } from '../../../shared/ui/Badge'
import { toast } from '../../../shared/ui/toastStore'
import type { RequestItemStatus } from '../../../shared/api/types'
import { patchItemInCache } from '../utils/patchItemInCache'

// Метки статусов позиций — включая новые бизнес-статусы
export const ITEM_STATUS_LABELS: Record<string, string> = {
  NEW: 'Новая',
  IN_QUEUE: 'В очереди',
  IN_PROGRESS: 'В работе',
  NEEDS_INFO: 'Нужна инфо',
  QUOTED: 'Квота получена',
  PARTIAL: 'Частично готова',
  READY_FOR_QUOTATION: 'Готова к КП',
  QUOTATION_CREATED: 'КП выставлено',
  ORDERED: 'Счёт',
  REJECTED_BY_CUSTOMER: 'Отказ клиента',
  REWORK: 'Доработка',
  NOT_FOUND: 'Не найдено',
  NOT_ACTUAL: 'Неактуальна',
  CANCELLED: 'Отменена',
  NO_RESPONSE: 'Нет ответа',
  // Legacy-значения не показываются в выпадашке (см. LEGACY_ITEM_STATUSES),
  // но нужны здесь для отображения старых записей из БД.
  WORK_PROCUREMENT: 'В работе (legacy)',
  WORK_SALES: 'В работе ОП (legacy)',
  KP_SENT: 'КП отправлено (legacy)',
  TO_PROCUREMENT: 'В закупку (legacy)',
  INVOICE: 'Счёт (legacy)',
  ARCHIVED: 'В архиве',
  NO_OFFER: 'Нет предложения (legacy)',
  REFUSED: 'Отказ клиента (legacy)',
}

// Для фильтра списка позиций — без legacy-значений и без NOT_FOUND
// (NOT_FOUND выставляется автостатусом, руками не выбирается).
const VISIBLE_STATUS_ENTRIES = Object.entries(ITEM_STATUS_LABELS).filter(
  ([value]) => !LEGACY_ITEM_STATUSES.has(value as RequestItemStatus) && value !== 'NOT_FOUND',
)

export const ITEM_STATUS_OPTIONS: { value: RequestItemStatus | ''; label: string }[] = [
  { value: '', label: 'Все статусы' },
  ...VISIBLE_STATUS_ENTRIES.map(([value, label]) => ({
    value: value as RequestItemStatus,
    label,
  })),
]

// Статусы, которые сервер принимает в PATCH /request-items/:id — та же схема, что
// валидирует запрос. «КП выставлено», «Счёт», «Отказ клиента», «Заказ» и «Не
// найдено» в неё не входят: их ставят создание КП, окно сделки, заказы Блока 2 и
// автостатус. Раньше выпадашка их предлагала, и выбор молча отклонялся валидацией.
const PATCHABLE_STATUSES = new Set<string>(RequestItemStatusSchema.options)

/**
 * Куда менеджер может перевести позицию вручную из текущего статуса: строгая карта
 * ITEM_TRANSITIONS из @crm/shared (с 14.09.2026 сервер отклоняет остальное) ∩
 * статусы, принимаемые правкой позиции.
 */
export function manualStatusTargets(current: RequestItemStatus): RequestItemStatus[] {
  const allowed = ITEM_TRANSITIONS[current as keyof typeof ITEM_TRANSITIONS] ?? []
  return allowed.filter(
    (s) => PATCHABLE_STATUSES.has(s) && !LEGACY_ITEM_STATUSES.has(s),
  ) as RequestItemStatus[]
}

function statusLabel(status: string): string {
  return (
    ITEM_STATUS_LABELS[status] ??
    ITEM_STATUS_LABELS_RU[status as keyof typeof ITEM_STATUS_LABELS_RU] ??
    status
  )
}

/**
 * Какие per-user state закупщика заменяют item.status у менеджера/админа.
 * Это финальные/«ручные» исходы работы закупки — менеджеру важно видеть, что
 * по позиции уже отработали и предложить нечего (NOT_FOUND/NO_RESPONSE/REFUSED)
 * либо нужна его реакция (WAITING/NEEDS_INFO). QUOTED и NEW/REQUESTED не
 * замещают — там бизнес-цикл идёт своим чередом (менеджерский item.status).
 */
const TEAM_STATE_OVERRIDES_STATUS = new Set<RequestItemProcurementState>([
  'WAITING',
  'NEEDS_INFO',
  'NO_RESPONSE',
  'REFUSED',
  'NOT_FOUND',
])

export function StatusCell({
  itemId,
  currentStatus,
  requestId,
  userRole,
  teamProcurementState,
  procurementState,
  hasAnyQuote = false,
}: {
  itemId: string
  currentStatus: RequestItemStatus
  requestId: string
  userRole?: string
  /** Агрегат команды закупщиков (для MANAGER/ADMIN/ROP). */
  teamProcurementState?: RequestItemProcurementState
  /** Personal state (для PROCUREMENT/ROS). */
  procurementState?: RequestItemProcurementState
  /** Есть ли хоть одна квота у позиции (любой автор). */
  hasAnyQuote?: boolean
}) {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)

  // Для PROCUREMENT / ROS — их закупщицкий state, без возможности редактирования
  // через глобальный селект. Менять state они могут на странице позиции.
  if (userRole === 'PROCUREMENT' || userRole === 'ROS') {
    const st = procurementState ?? 'NEW'
    return <ProcItemStatusBadge state={st} team={userRole === 'ROS'} />
  }

  // Для MANAGER / ADMIN / ROP — если квот ещё нет И команда ручным статусом
  // уже обозначила позицию («Жду ответа», «Нужна инфо», «Отказ»,
  // «Поставщик не ответил») — показываем закупщицкий бейдж вместо item.status.
  // Когда появится первая квота — показываем обычный item.status (обычно
  // READY_FOR_QUOTATION / QUOTED из автоматики).
  if (
    !hasAnyQuote &&
    teamProcurementState &&
    TEAM_STATE_OVERRIDES_STATUS.has(teamProcurementState)
  ) {
    return <ProcItemStatusBadge state={teamProcurementState} team />
  }

  const targets = manualStatusTargets(currentStatus)

  const handleChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newStatus = e.target.value as RequestItemStatus
    if (!newStatus || newStatus === currentStatus) {
      setEditing(false)
      return
    }
    setSaving(true)
    try {
      await patchItem(itemId, { status: newStatus })
      // Оптимистично обновляем только эту позицию в кэше — без рефетча
      // всей заявки. Помечаем как stale, чтобы при следующей навигации
      // данные подтянулись свежими.
      patchItemInCache(queryClient, requestId, itemId, { status: newStatus })
      queryClient.invalidateQueries({ queryKey: ['requests', requestId], refetchType: 'none' })
    } catch (err) {
      // Сервер проверяет переход по той же карте — отказ возможен, если статус
      // позиции успел смениться (другой пользователь, автостатус). Раньше ошибка
      // терялась молча: выпадашка закрывалась, статус оставался прежним.
      const msg =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Не удалось изменить статус позиции'
      toast.error(msg)
    } finally {
      setSaving(false)
      setEditing(false)
    }
  }

  // Из статуса нет ручных переходов (legacy/служебный) — выпадашку не открываем.
  if (targets.length === 0) {
    return (
      <div title="Статус меняется автоматически">
        <ItemStatusBadge status={currentStatus} />
      </div>
    )
  }

  if (editing) {
    return (
      <select
        autoFocus
        defaultValue={currentStatus}
        onChange={handleChange}
        onBlur={() => setEditing(false)}
        disabled={saving}
        className="w-full text-xs border border-brand-300 rounded px-1 py-0.5 bg-white focus:outline-none focus:ring-1 focus:ring-brand-400"
      >
        {[currentStatus, ...targets].map((v) => (
          <option key={v} value={v}>
            {statusLabel(v)}
          </option>
        ))}
      </select>
    )
  }

  return (
    <div onClick={() => setEditing(true)} title="Клик — изменить статус" className="cursor-pointer">
      <ItemStatusBadge status={currentStatus} />
    </div>
  )
}
